/* eslint-disable camelcase */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import SnackbarProvider from 'react-simple-snackbar';
import { Auth0Provider } from '@auth0/auth0-react';
import { ConfigProvider } from 'antd';
import querystring from 'query-string';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import Application from '@totem/components/Application';
import { lightThemeData } from '@totem/styles/theme';
import configuration from '@totem/utilities/configuration';
import { installSessionExpiryHandling } from '@totem/utilities/sessionExpiry';

type AuthProviderProps = {
  children: React.ReactNode;
};

const Auth0ProviderWithNavigate = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    navigate(appState?.returnTo || '/', { replace: true });
  };

  const authorizationParams = {
    redirect_uri: configuration.AUTH_CALLBACK_URL,
    audience: configuration.AUTH_AUDIENCE,
    scope: configuration.AUTH_SCOPE,
  };

  const skipRedirectCallback = window.location.pathname === '/auth/callback';

  return (
    <Auth0Provider
      domain={configuration.AUTH_DOMAIN}
      clientId={configuration.AUTH_CLIENT_ID}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="memory"
      skipRedirectCallback={skipRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

// Public pages (login, public ticket/survey links) always use the light theme.
// Signed-in users get their saved light/dark/os preference from AuthorizedRoute.
const ThemedApp = () => {
  return (
    <ConfigProvider theme={lightThemeData}>
      <Application />
    </ConfigProvider>
  );
};

const initializeApplication = () => {
  installSessionExpiryHandling();

  const root = createRoot(document.getElementById('application'));
  root.render(
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <QueryParamProvider
          adapter={ReactRouter6Adapter}
          options={{
            searchStringToObject: querystring.parse,
            objectToSearchString: querystring.stringify,
          }}
        >
          <SnackbarProvider>
            <ThemedApp />
          </SnackbarProvider>
        </QueryParamProvider>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>,
  );
};

const Root = {
  initializeApplication,
};

export default Root;
